import React, { useState, useEffect } from 'react';
import { merchantService, Organization } from '../../services/merchantService';

interface OrganizationSelectorProps {
  value?: string;
  onChange: (organizationId: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Sélectionnez une marque globale',
  className = ''
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await merchantService.getOrganizations();
        setOrganizations(response.organizations);
      } catch (err) {
        console.error('Error loading organizations:', err);
        setError('Impossible de charger les marques globales');
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
        >
          <option value="">Chargement des marques globales...</option>
        </select>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm bg-red-50 text-red-500"
        >
          <option value="">{error}</option>
        </select>
        <p className="mt-1 text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <select
        value={value || ''}
        onChange={handleChange}
        disabled={disabled || organizations.length === 0}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
      >
        <option value="">{placeholder}</option>
        {organizations.map((org) => (
          <option key={org.organization_id} value={org.organization_id}>
            {org.name} {!org.is_active && '(Inactive)'}
          </option>
        ))}
      </select>
      {organizations.length === 0 && !loading && (
        <p className="mt-1 text-sm text-gray-500">Aucune marque globale disponible</p>
      )}
    </div>
  );
};

export default OrganizationSelector;